import { useContext, useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';

import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  IconButton,
  useTheme,
} from '@mui/material';

import { AttachFile, DeleteOutline } from '@mui/icons-material';

import MessageVariablesPicker from '../MessageVariablesPicker';
import ConfirmationModal from '../ConfirmationModal';

import { i18n } from '../../translate/i18n';
import { head } from 'lodash';
import api from '../../services/api';
import toastError from '../../toasts/toastError';
import { AuthContext } from '../../context/Auth/AuthContext';
import * as path from 'path';
import { getStyled } from './styles';

const QuickeMessageSchema = Yup.object().shape({
  shortcode: Yup.string().required('Obrigatório'),
});

const QuickMessageDialog = ({ open, onClose, quickemessageId, reload }) => {
  const theme = useTheme();
  const classes = getStyled(theme);
  const { user } = useContext(AuthContext);

  const messageInputRef = useRef();

  const initialState = {
    shortcode: '',
    message: '',
    geral: false,
    status: true,
  };

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [quickemessage, setQuickemessage] = useState(initialState);
  const [attachment, setAttachment] = useState(null);
  const attachmentFile = useRef(null);

  useEffect(() => {
    try {
      (async () => {
        if (!quickemessageId) return;

        const { data } = await api.get(`/quick-messages/${quickemessageId}`);

        setQuickemessage((prevState) => {
          return { ...prevState, ...data };
        });
      })();
    } catch (err) {
      toastError(err);
    }
  }, [quickemessageId, open]);

  const handleClose = () => {
    setQuickemessage(initialState);
    setAttachment(null);
    onClose();
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      setAttachment(file);
    }
  };

  const handleSaveQuickeMessage = async (values) => {
    const quickemessageData = {
      ...values,
      isMedia: true,
      mediaPath: attachment
        ? String(attachment.name).replace(/ /g, '_')
        : values.mediaPath
          ? path.basename(values.mediaPath).replace(/ /g, '_')
          : null,
    };

    try {
      if (quickemessageId) {
        await api.put(`/quick-messages/${quickemessageId}`, quickemessageData);
        if (attachment != null) {
          const formData = new FormData();
          formData.append('typeArch', 'quickMessage');
          formData.append('file', attachment);
          await api.post(
            `/quick-messages/${quickemessageId}/media-upload`,
            formData,
          );
        }
      } else {
        const { data } = await api.post('/quick-messages', quickemessageData);
        if (attachment != null) {
          const formData = new FormData();
          formData.append('typeArch', 'quickMessage');
          formData.append('file', attachment);
          await api.post(`/quick-messages/${data.id}/media-upload`, formData);
        }
      }
      toastSuccess(i18n.t('quickMessages.toasts.success'));
      if (typeof reload == 'function') {
        reload();
      }
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      attachmentFile.current.value = null;
    }

    if (quickemessage.mediaPath) {
      await api.delete(`/quick-messages/${quickemessage.id}/media-upload`);
      setQuickemessage((prev) => ({
        ...prev,
        mediaPath: null,
      }));
      toastSuccess(i18n.t('quickMessages.toasts.deleted'));
      if (typeof reload == 'function') {
        reload();
      }
    }
  };

  const handleClickMsgVar = async (msgVar, setValueFunc) => {
    const el = messageInputRef.current;
    const firstHalfText = el.value.substring(0, el.selectionStart);
    const secondHalfText = el.value.substring(el.selectionEnd);
    const newCursorPos = el.selectionStart + msgVar.length;

    setValueFunc('message', `${firstHalfText}${msgVar}${secondHalfText}`);

    await new Promise((r) => setTimeout(r, 100));
    messageInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
  };

  return (
    <div style={classes.root}>
      <ConfirmationModal
        title={i18n.t('quickMessages.confirmationModal.deleteTitle')}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t('quickMessages.confirmationModal.deleteMessage')}
      </ConfirmationModal>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth='xs'
        fullWidth
        scroll='paper'
      >
        <DialogTitle id='form-dialog-title' sx={theme.custom.dialogColor}>
          {quickemessageId
            ? `${i18n.t('quickMessages.dialog.edit')}`
            : `${i18n.t('quickMessages.dialog.add')}`}
        </DialogTitle>
        <div style={{ display: 'none' }}>
          <input
            type='file'
            ref={attachmentFile}
            onChange={(e) => handleAttachmentFile(e)}
          />
        </div>
        <Formik
          initialValues={quickemessage}
          enableReinitialize={true}
          validationSchema={QuickeMessageSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveQuickeMessage(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, setFieldValue, values }) => (
            <Form>
              <DialogContent dividers sx={theme.custom.dialogColor}>
                <Grid spacing={1} container>
                  <Grid size={{ xs: 12 }}>
                    <Field
                      as={TextField}
                      autoFocus
                      label={i18n.t('quickMessages.dialog.shortcode')}
                      name='shortcode'
                      disabled={
                        quickemessageId &&
                        values.visao &&
                        !values.geral &&
                        values.userId !== user.id
                      }
                      error={touched.shortcode && Boolean(errors.shortcode)}
                      helperText={touched.shortcode && errors.shortcode}
                      variant='outlined'
                      margin='dense'
                      fullWidth
                      sx={theme.custom.textField}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Field
                      as={TextField}
                      label={i18n.t('quickMessages.dialog.message')}
                      name='message'
                      inputRef={messageInputRef}
                      error={touched.message && Boolean(errors.message)}
                      helperText={touched.message && errors.message}
                      variant='outlined'
                      margin='dense'
                      disabled={
                        quickemessageId &&
                        values.visao &&
                        !values.geral &&
                        values.userId !== user.id
                      }
                      multiline={true}
                      rows={7}
                      fullWidth
                      sx={theme.custom.textField}
                    />
                  </Grid>
                  <Grid>
                    <MessageVariablesPicker
                      disabled={
                        isSubmitting ||
                        (quickemessageId &&
                          values.visao &&
                          !values.geral &&
                          values.userId !== user.id)
                      }
                      onClick={(value) =>
                        handleClickMsgVar(value, setFieldValue)
                      }
                    />
                  </Grid>
                  {/* {(profile === "admin" || profile === "supervisor") && ( */}
                  <Grid size={{ xs: 12 }}>
                    <FormControl variant='outlined' margin='dense' fullWidth>
                      <InputLabel
                        id='geral-selection-label'
                        sx={theme.custom.label}
                      >
                        {i18n.t('quickMessages.dialog.visao')}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t('quickMessages.dialog.visao')}
                        placeholder={i18n.t('quickMessages.dialog.visao')}
                        labelId='visao-selection-label'
                        id='visao'
                        disabled={
                          quickemessageId &&
                          values.visao &&
                          !values.geral &&
                          values.userId !== user.id
                        }
                        name='visao'
                        onChange={(e) => {
                          setFieldValue('visao', e.target.value === 'true');
                        }}
                        error={touched.visao && Boolean(errors.visao)}
                        value={values.visao ? 'true' : 'false'}
                        sx={theme.custom.textField}
                        MenuProps={theme.custom.menuProps}
                      >
                        <MenuItem value={'true'}>
                          {i18n.t('announcements.active')}
                        </MenuItem>
                        <MenuItem value={'false'}>
                          {i18n.t('announcements.inactive')}
                        </MenuItem>
                      </Field>
                    </FormControl>
                    {/* Renderização condicional do novo item */}
                    {values.visao === true && (
                      <FormControl variant='outlined' margin='dense' fullWidth>
                        <InputLabel
                          id='geral-selection-label'
                          sx={theme.custom.label}
                        >
                          {i18n.t('quickMessages.dialog.geral')}
                        </InputLabel>
                        <Field
                          as={Select}
                          label={i18n.t('quickMessages.dialog.geral')}
                          placeholder={i18n.t('quickMessages.dialog.geral')}
                          labelId='novo-item-selection-label'
                          id='geral'
                          name='geral'
                          disabled={
                            quickemessageId &&
                            values.visao &&
                            !values.geral &&
                            values.userId !== user.id
                          }
                          value={values.geral ? 'true' : 'false'}
                          error={touched.geral && Boolean(errors.geral)}
                          sx={theme.custom.textField}
                          MenuProps={theme.custom.menuProps}
                        >
                          <MenuItem value={'true'}>
                            {i18n.t('announcements.active')}
                          </MenuItem>
                          <MenuItem value={'false'}>
                            {i18n.t('announcements.inactive')}
                          </MenuItem>
                        </Field>
                      </FormControl>
                    )}
                  </Grid>
                  {(quickemessage.mediaPath || attachment) && (
                    <Grid size={{ xs: 12 }}>
                      <Button startIcon={<AttachFile />}>
                        {attachment ? attachment.name : quickemessage.mediaName}
                      </Button>
                      <IconButton
                        onClick={() => setConfirmationOpen(true)}
                        disabled={
                          quickemessageId &&
                          values.visao &&
                          !values.geral &&
                          values.userId !== user.id
                        }
                      >
                        <DeleteOutline sx={theme.custom.colors.cancelButton} />
                      </IconButton>
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions sx={classes.dialogActions}>
                {!attachment && !quickemessage.mediaPath && (
                  <Button
                    onClick={() => attachmentFile.current.click()}
                    disabled={
                      isSubmitting ||
                      (quickemessageId &&
                        values.visao &&
                        !values.geral &&
                        values.userId !== user.id)
                    }
                    variant='outlined'
                    sx={theme.custom.editButton}
                  >
                    {i18n.t('quickMessages.buttons.attach')}
                  </Button>
                )}
                <Button
                  onClick={handleClose}
                  disabled={isSubmitting}
                  variant='outlined'
                  sx={theme.custom.cancelButton}
                >
                  {i18n.t('quickMessages.buttons.cancel')}
                </Button>
                <Button
                  type='submit'
                  disabled={
                    isSubmitting ||
                    (quickemessageId &&
                      values.visao &&
                      !values.geral &&
                      values.userId !== user.id)
                  }
                  variant='outlined'
                  sx={theme.custom.confirmButton}
                >
                  {quickemessageId
                    ? `${i18n.t('quickMessages.buttons.edit')}`
                    : `${i18n.t('quickMessages.buttons.add')}`}
                  {isSubmitting && (
                    <CircularProgress size={24} sx={classes.buttonProgress} />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default QuickMessageDialog;
